import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ionara_token";
const AuthContext = createContext(null);

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, object = logged in

  const fetchMe = useCallback(async () => {
    const tok = getToken();
    if (!tok) {
      setUser(false);
      return;
    }
    try {
      const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setUser(d.user);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const post = async (path, body) => {
    const r = await fetch(`${API}/api/auth/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatDetail(d.detail));
    localStorage.setItem(TOKEN_KEY, d.token);
    setUser(d.user);
    return d.user;
  };

  const login = (email, password) => post("login", { email, password });
  const register = (email, password, sessionId) =>
    post("register", sessionId ? { email, password, session_id: sessionId } : { email, password });
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const plainPost = async (path, body) => {
    const r = await fetch(`${API}/api/auth/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatDetail(d.detail));
    return d;
  };

  const forgotPassword = (email, originUrl) => plainPost("forgot-password", { email, origin_url: originUrl });
  const resetPassword = (token, password) => plainPost("reset-password", { token, password });

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: fetchMe, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
